import * as React from 'react';
import { Avatar, Button, List, Popconfirm, Space, Spin, Typography } from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  StopOutlined,
} from '@ant-design/icons';
import Timestamp from 'components/helpers/Timestamp';
import { useTranslation } from 'translations';
import { SUPPORTED_CODES } from '../util/params';

interface GenericNotificationProps {
  notification: any;
  title?: string;
  description?: string | React.ReactNode;
  deleteNotification: (...args: any[]) => any;
  extra?: React.ReactNode;
  onClick: (...args: any[]) => any;
  isDeleting: boolean;
  icon?: React.ReactNode;
  setDropdownVisible?: (...args: unknown[]) => unknown;
  updateNotificationStatus?: (...args: unknown[]) => unknown;
}

const GenericNotification = React.memo((
    {
      notification,
      title,
      description,
      deleteNotification,
      extra = null,
      onClick,
      isDeleting,
      icon = null,
    }: GenericNotificationProps,
  ) => {
    const { tC } = useTranslation('features', 'notifications');

    const sendDeleteNotification = () => {
      deleteNotification(notification);
    };

    let avatarColor;
    let avatarIcon;
    let altTitle;
    switch (notification.read) {
      case 'unread':
        switch (notification.base.type) {
          case 'permission':
            avatarIcon = <KeyOutlined />;
            break;
          default:
            avatarColor = '#0094f9';
            avatarIcon = <InfoCircleOutlined />;
        }
        break;
      case 'read':
        switch (notification.base.type) {
          case 'permission':
            avatarIcon = <KeyOutlined />;
            break;
          default:
            avatarColor = '#aaa';
            avatarIcon = <InfoCircleOutlined />;
        }
        break;
      case 'accepted':
        avatarColor = 'green';
        avatarIcon = <CheckCircleOutlined />;
        break;
      case 'denied':
        avatarColor = 'red';
        avatarIcon = <StopOutlined />;
        break;
      case 'deleted':
        avatarColor = 'red';
        avatarIcon = <ExclamationCircleOutlined />;
        break;
      default:
        avatarColor = '#aaa';
        avatarIcon = <InfoCircleOutlined />;
    }
    switch (notification.base.type) {
      case 'wallet':
        altTitle = tC('fromType.wallet');
        break;
      default:
        altTitle = tC('fromType.wappsto');
    }

    return (
      <List.Item key={notification.meta.id} onClick={onClick}>
        <List.Item.Meta
          avatar={
            icon ||
            (isDeleting ? (
              <Spin />
            ) : (
              <Avatar style={{ backgroundColor: avatarColor }} icon={avatarIcon} />
            ))
          }
          title={
            <Typography.Text strong={notification.read === 'unread'}>
              {description ||
                (SUPPORTED_CODES.includes(notification.base.code) &&
                  tC(`baseCode.${notification.base.code}`, { notification }))}
            </Typography.Text>
          }
          description={
            <Space orientation="vertical">
              {extra && <div>{extra}</div>}

              {title !== null && (
                <Typography.Text type={notification.read === 'unread' ? undefined : 'secondary'}>
                  {title || altTitle}
                </Typography.Text>
              )}
            </Space>
          }
        />
        <Space orientation="vertical" align="end">
          <Popconfirm
            title={tC('notificationDeleteConfirm')}
            okText={tC('notificationDeleteButton')}
            okType="danger"
            cancelText={tC('cancelButton')}
            onConfirm={sendDeleteNotification}
            overlayStyle={{ zIndex: 9999 }}
            placement="bottom">
            <Button size="small" type="default">
              {tC('dismissButton')}
            </Button>
          </Popconfirm>
          <Timestamp timestamp={notification.timestamp} fromNow className="font-sm" />
        </Space>
      </List.Item>
    );
  },
);

GenericNotification.displayName = 'GenericNotification';

export default GenericNotification;
